import { DateTime, type DurationLikeObject } from "luxon";

import LocalForagePlugin, { syncState } from "@/plugins/LocalForagePlugin";
import { batchSetItems, batchRemoveItems } from "@/utils/localforage-batch";
import { goFetch } from "@/utils/network-utils";
import type { ApiFetchResponse, PersistedQueryPayload } from "@tmi/types/api/fetch";

import { createDefaultLogger, LogLevels } from "@/types/console-logger";
const logger = createDefaultLogger( LogLevels.DEBUG );

export interface DependencySyncConfig<TResponse, TApi, TLocal extends { id: string }> {
    name: string;
    store: () => LocalForage;
    syncKey: string;
    throttle: DurationLikeObject;
    queryId: string;
    pageSize: number;
    endpoint?: string;
    extractPayload: ( data: ApiFetchResponse<TResponse> ) => TApi[] | undefined | null;
    mapItem: ( item: TApi ) => TLocal;
    postProcess?: ( items: TLocal[] ) => TLocal[];
}

export interface DependencyFetchResult<TLocal> {
    items: TLocal[];
    /**
     * False when a page was lost, i.e. `items` is a subset of what the server holds. A truncated
     * set must never be used to decide that records were deleted upstream — see `syncDependency`.
     */
    complete: boolean;
}

export async function fetchFromServer<TResponse, TApi, TLocal extends { id: string }>(
    config: DependencySyncConfig<TResponse, TApi, TLocal>,
    authToken?: string | null
): Promise<DependencyFetchResult<TLocal>> {
    const t0 = performance.now();
    let allItems: TLocal[] = [];
    let page = 0;
    let complete = true;
    let pagePayload: TLocal[] = [];

    do {
        const response =
            await goFetch<TResponse, PersistedQueryPayload>( {
                serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                endpoint: config.endpoint ?? `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                payload: {
                    id: config.queryId,
                    variables: {
                        skip: page,
                        limit: config.pageSize
                    }
                },
                authToken: authToken ?? undefined
            } );

        if ( !response ) {
            // A failed request is indistinguishable from "the server has nothing more" once the
            // loop exits, so record it explicitly rather than letting the caller assume the set
            // is authoritative and prune against it.
            complete = false;
            logger.warn( `[ ${ config.name }-sync-coordinator ] page ${ page } request failed — treating the result set as partial` );
            break;
        }

        const raw = config.extractPayload( response );
        if ( raw ) {
            pagePayload = raw.map( config.mapItem );

            const t1 = performance.now();
            logger.info( `Fetching & mapping ${ pagePayload.length } ${ config.name } items took ${ t1 - t0 } ms` );

            allItems = [ ...pagePayload, ...allItems ];
        } else {
            complete = false;
            logger.warn( `[ ${ config.name }-sync-coordinator ] page ${ page } carried no payload — treating the result set as partial` );
            break;
        }

        page++;
    } while ( pagePayload.length >= config.pageSize );

    if ( config.postProcess ) {
        allItems = config.postProcess( allItems );
    }

    return { items: allItems, complete };
}

export async function syncDependency<TResponse, TApi, TLocal extends { id: string }>(
    config: DependencySyncConfig<TResponse, TApi, TLocal>,
    map?: Map<string, TLocal>,
    authToken?: string | null
): Promise<boolean> {
    let updated = false;
    try {
        const t0 = performance.now();

        if ( typeof syncState === 'undefined' ) {
            await LocalForagePlugin.initialize();
        }

        const store = config.store();

        await syncState.ready();
        const syncThresholdInMillis = await syncState.getItem<number>( config.syncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
            const { items: received, complete } = await fetchFromServer( config, authToken );

            if ( received.length > 0 ) {
                updated = true;

                await store.ready();

                // WRITE FIRST. This used to delete before writing, so any failure in between left
                // the store emptier than it started and the user staring at missing data.
                await batchSetItems( store, received.map( item => [ item.id, item ] as const ) );

                // Purge IDB entries that no longer exist on the server — but only when the fetch
                // came back whole. Pruning against a truncated page set would delete records that
                // are alive on the server and merely absent from a failed response.
                let keysToRemove: string[] = [];
                if ( complete ) {
                    const receivedIds = new Set( received.map( item => item.id ) );
                    await store.iterate( ( _: TLocal, key: string ) => {
                        if ( !receivedIds.has( key ) ) { keysToRemove.push( key ); }
                        return;
                    } );
                    await batchRemoveItems( store, keysToRemove );
                } else {
                    logger.warn( `[ ${ config.name }-sync-coordinator ] partial fetch — skipping the stale-record purge` );
                }

                // Commit the long throttle only for a clean pass; retry a partial one shortly.
                const nextSyncThresholdInMillis = complete
                    ? DateTime.utc().plus( config.throttle ).toMillis()
                    : DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( config.syncKey, nextSyncThresholdInMillis );

                const t1 = performance.now();
                logger.info( `[ sync-worker ] Refreshed ${ received.length } ${ config.name } items from server in ${ t1 - t0 } ms (${ keysToRemove.length } stale removed)` );

                // Populate map directly from server data — skip redundant IDB re-read.
                // On a partial fetch the map would be missing live records, so fall through to
                // the IDB-backed hydration below, which still holds everything we had before.
                if ( map && complete ) {
                    map.clear();
                    received.forEach( item => map.set( item.id, item ) );
                    return updated;
                }
            } else {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( config.syncKey, nextSyncThresholdInMillis );
            }
        } else if ( map && map.size > 0 ) {
            return updated;
        }

        // Cold start: populate map from IDB cache
        if ( map ) {
            map.clear();
            await store.ready();
            await store.iterate( ( item: TLocal ) => {
                map.set( item.id, item );
                return;
            } );
        }
    } catch ( err: any ) {
        logger.error( `[ ${ config.name }-sync-coordinator ] Sync failed:`, err );
    }

    return updated;
}
